import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import colors from '../../styles/colors';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';
import Button from '../Button';

/** Keep tooltip clear of the larger 2026-007 highlight ring. */
const TOOLTIP_TARGET_GAP_PX = 25;

const TooltipContainer = styled.div`
  position: absolute;
  background: linear-gradient(to right, #32618f, #231f20);
  border-radius: 8px;
  padding: 20px;
  min-width: 300px;
  max-width: 400px;
  z-index: 1002;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const TooltipTitle = styled.h3`
  color: ${colors.white};
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const TooltipProgress = styled.div`
  color: ${colors.white};
  font-size: 12px;
  margin-bottom: 15px;
  opacity: 0.9;
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(to right, #c7e6d8, #016c60);
  margin: 15px 0;
`;

const TooltipFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  gap: 10px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const StepContent = styled.div`
  color: ${colors.white};
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const Arrow = styled.div`
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  ${(props) => {
  const { placement, pointerPosition } = props;
  const pointerPos = pointerPosition || 'center';

  if (placement === 'top') {
    const leftPos = pointerPos === 'left' ? '20px' : pointerPos === 'right' ? 'auto' : '50%';
    const rightPos = pointerPos === 'right' ? '20px' : 'auto';
    const transform = pointerPos === 'center' ? 'translateX(-50%)' : 'none';

    return `
        bottom: -13px;
        left: ${leftPos};
        right: ${rightPos};
        transform: ${transform};
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid #231f20;
      `;
  }

  if (placement === 'bottom') {
    const leftPos = pointerPos === 'left' ? '20px' : pointerPos === 'right' ? 'auto' : '50%';
    const rightPos = pointerPos === 'right' ? '20px' : 'auto';
    const transform = pointerPos === 'center' ? 'translateX(-50%)' : 'none';

    return `
        top: -13px;
        left: ${leftPos};
        right: ${rightPos};
        transform: ${transform};
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid #231f20;
      `;
  }

  if (placement === 'left') {
    const topPos = pointerPos === 'top' ? '20px' : pointerPos === 'bottom' ? 'auto' : '50%';
    const bottomPos = pointerPos === 'bottom' ? '20px' : 'auto';
    const transform = pointerPos === 'center' ? 'translateY(-50%)' : 'none';

    return `
        right: -13px;
        top: ${topPos};
        bottom: ${bottomPos};
        transform: ${transform};
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-left: 10px solid #231f20;
      `;
  }

  if (placement === 'right') {
    const topPos = pointerPos === 'top' ? '20px' : pointerPos === 'bottom' ? 'auto' : '50%';
    const bottomPos = pointerPos === 'bottom' ? '20px' : 'auto';
    const transform = pointerPos === 'center' ? 'translateY(-50%)' : 'none';

    return `
        left: -13px;
        top: ${topPos};
        bottom: ${bottomPos};
        transform: ${transform};
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        border-right: 10px solid #231f20;
      `;
  }

  return `
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid #231f20;
    `;
}}
`;

export default function OnboardingTooltip({
  title,
  body,
  currentScreenIndex,
  totalScreens,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  showBack,
  onBack,
  onClose,
  targetElement,
  showCentered = false,
  placement = 'bottom',
  pointerPosition = 'center',
}) {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const positionCentered = () => {
      if (!tooltipRef.current) return;
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({
        top: window.innerHeight / 2 + scrollY - tooltipRect.height / 2,
        left: window.innerWidth / 2 + scrollX - tooltipRect.width / 2,
      });
    };

    /** Center when configured or when the anchor is missing (avoid top-left 0,0). */
    if (showCentered || !targetElement) {
      if (!tooltipRef.current) return undefined;
      positionCentered();
      window.addEventListener('resize', positionCentered);
      return () => window.removeEventListener('resize', positionCentered);
    }

    if (!tooltipRef.current) return undefined;

    const updatePosition = () => {
      const rect = targetElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      const place = placement || 'bottom';
      const pointerPos = pointerPosition || 'center';

      let top = 0;
      let left = 0;

      if (place === 'top') {
        top = rect.top + scrollY - tooltipRect.height - TOOLTIP_TARGET_GAP_PX;
        if (pointerPos === 'left') {
          left = rect.left + scrollX;
        } else if (pointerPos === 'right') {
          left = rect.right + scrollX - tooltipRect.width;
        } else {
          left = rect.left + scrollX + rect.width / 2 - tooltipRect.width / 2;
        }
      } else if (place === 'bottom') {
        top = rect.bottom + scrollY + TOOLTIP_TARGET_GAP_PX;
        if (pointerPos === 'left') {
          left = rect.left + scrollX;
        } else if (pointerPos === 'right') {
          left = rect.right + scrollX - tooltipRect.width;
        } else {
          left = rect.left + scrollX + rect.width / 2 - tooltipRect.width / 2;
        }
      } else if (place === 'left') {
        left = rect.left + scrollX - tooltipRect.width - TOOLTIP_TARGET_GAP_PX;
        if (pointerPos === 'top') {
          top = rect.top + scrollY;
        } else if (pointerPos === 'bottom') {
          top = rect.bottom + scrollY - tooltipRect.height;
        } else {
          top = rect.top + scrollY + rect.height / 2 - tooltipRect.height / 2;
        }
      } else if (place === 'right') {
        left = rect.right + scrollX + TOOLTIP_TARGET_GAP_PX;
        if (pointerPos === 'top') {
          top = rect.top + scrollY;
        } else if (pointerPos === 'bottom') {
          top = rect.bottom + scrollY - tooltipRect.height;
        } else {
          top = rect.top + scrollY + rect.height / 2 - tooltipRect.height / 2;
        }
      } else {
        top = rect.bottom + scrollY + TOOLTIP_TARGET_GAP_PX;
        left = rect.left + scrollX + rect.width / 2 - tooltipRect.width / 2;
      }

      const padding = 20;
      if (left < padding) left = padding;
      if (left + tooltipRect.width > window.innerWidth - padding) {
        left = window.innerWidth - tooltipRect.width - padding;
      }
      if (top < padding) top = padding;
      if (top + tooltipRect.height > window.innerHeight + scrollY - padding) {
        top = window.innerHeight + scrollY - tooltipRect.height - padding;
      }

      setPosition({ top, left });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [targetElement, placement, pointerPosition, showCentered]);

  const place = placement || 'bottom';
  const pointerPos = pointerPosition || 'center';

  return (
    <TooltipContainer
      ref={tooltipRef}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      {!showCentered && targetElement && (
        <Arrow placement={place} pointerPosition={pointerPos} />
      )}
      <TooltipHeader>
        <TooltipTitle>{title}</TooltipTitle>
        <Icon
          icon={Icons.TimesCircleSolid}
          onClick={onClose}
          size={18}
          activeColor={colors.white}
        />
      </TooltipHeader>
      <TooltipProgress>
        Step {currentScreenIndex + 1} of {totalScreens}
      </TooltipProgress>
      <StepContent>{body}</StepContent>
      <Divider />
      <TooltipFooter>
        <ButtonGroup>
          {showBack && (
            <Button
              text="Back"
              onClick={onBack}
              width="80px"
              height="35px"
            />
          )}
          <Button
            text={primaryLabel}
            onClick={onPrimary}
            width="max-content"
            height="35px"
            padding="0 14px"
            disabled={primaryDisabled}
          />
        </ButtonGroup>
      </TooltipFooter>
    </TooltipContainer>
  );
}
